import {ProcessTaskStatusType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ProcessTaskStatus} from 'processes/__generated/ProcessTaskStatus';
import {DefaultStatusDisplayName} from 'processes/ProcessConstants';

import {ProcessTaskStatusLifecycleGraphQL as ProcessTaskStatusLifecycle} from '../../../graphql-fragments/processes/__generated/ProcessTaskStatusLifecycleGraphQL';

export function isTerminalStatus(statusId: string, statusLifecycle: ProcessTaskStatusLifecycle): boolean {
  return !statusLifecycle.transitions.some((transition) => transition.inStatus.id === statusId);
}

export function getNextCompletedStatus(
  currentStatus: ProcessTaskStatus,
  statusLifecycle: ProcessTaskStatusLifecycle
): ProcessTaskStatus | null {
  const isStatusCompleted = (status: ProcessTaskStatus): boolean =>
    status.displayName === DefaultStatusDisplayName.COMPLETED ||
    status.displayName === DefaultStatusDisplayName.COMPLETED_AFTER_REVIEW;

  if (isStatusCompleted(currentStatus)) {
    return currentStatus;
  }
  const transition = statusLifecycle.transitions.find(
    (transition) => transition.inStatus.id === currentStatus.id && isStatusCompleted(transition.outStatus)
  );
  return transition?.outStatus ?? null;
}

export function getStatusFromId(
  statusId: string,
  statusLifecycle: ProcessTaskStatusLifecycle
): ProcessTaskStatus | null {
  const transition = statusLifecycle.transitions.find(
    (transition) => transition.inStatus.id === statusId || transition.outStatus.id === statusId
  );
  return transition?.inStatus.id === statusId ? transition.inStatus : (transition?.outStatus ?? null);
}

export function findValidOutStatuses(
  status: ProcessTaskStatus,
  statusLifecycle: ProcessTaskStatusLifecycle
): Array<ProcessTaskStatus> {
  return (isTerminalStatus(status.id, statusLifecycle) ? [] : [status]).concat(
    ...statusLifecycle.transitions
      .filter((transition) => transition.inStatus.id === status.id && !transition.isAutomatedTransition)
      .map((transition) => transition.outStatus)
  );
}

export const TASK_STATUS_TYPE_WEIGHT: {[key in ProcessTaskStatusType]: number} = {
  [ProcessTaskStatusType.PENDING]: 0,
  [ProcessTaskStatusType.IN_PROGRESS]: 1,
  [ProcessTaskStatusType.FAILED]: 2,
  [ProcessTaskStatusType.CANCELLED]: 3,
  [ProcessTaskStatusType.INVALID]: 4,
  [ProcessTaskStatusType.COMPLETED]: 5,
  [ProcessTaskStatusType.SKIPPED]: 6,
};

const TASK_STATUS_WEIGHT: {[key: string]: {[k in DefaultStatusDisplayName]?: number}} = {
  [ProcessTaskStatusType.IN_PROGRESS]: {
    [DefaultStatusDisplayName.IN_PROGRESS]: 0,
    [DefaultStatusDisplayName.COMPLETED_AFTER_REVIEW]: 1,
  },
};

/*
 * Sorts statuses by statusType using TASK_STATUS_WEIGHT and displayName to break ties.
 */
export function sortTaskStatuses(statuses: Array<ProcessTaskStatus>): Array<ProcessTaskStatus> {
  return [...statuses].sort((prev, next) => {
    // Primary sort by statusType
    if (prev.statusType !== next.statusType) {
      return TASK_STATUS_TYPE_WEIGHT[prev.statusType] - TASK_STATUS_TYPE_WEIGHT[next.statusType];
    }
    // Secondary sort on TASK_STATUS_WEIGHT
    const prevNameWeight =
      TASK_STATUS_WEIGHT?.[prev.statusType]?.[prev.displayName as DefaultStatusDisplayName] ?? 0;
    const nextNameWeight =
      TASK_STATUS_WEIGHT?.[prev.statusType]?.[next.displayName as DefaultStatusDisplayName] ?? 0;
    if (prevNameWeight !== nextNameWeight) {
      return prevNameWeight - nextNameWeight;
    }
    // Break ties on displayName
    return prev.displayName.localeCompare(next.displayName);
  });
}
